import React, { useState, useEffect } from 'react';
import { DashboardApi } from '../../../apis/DashboardApi';
import { ChevronDown, XCircle, Play, X } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { AxiosResponse } from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { shared } from "../../../reducer/SharedSlice";

interface FormData {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    contact_id: string;
    group_id: string;
    profile_image?: File;
}

interface Group {
    id: number;
    name: string;
}

const AddRecipient = () => {
    const [formData, setFormData] = useState<FormData>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        contact_id: 'None',
        group_id: 'Others'
    });

    const [groups, setGroups] = useState<Group[]>([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState<any>('');
    const [previewImage, setPreviewImage] = useState<string>('');
    const [recipientLength, setRecipientLength] = useState('');
    const [errors, setErrors] = useState<any>({});
    const [demoModalOpen, setDemoModalOpen] = useState(false);

    const dispatch = useDispatch();

    const userLoginData = useSelector((state: any) => state.data.login.value);
    const sharedData = useSelector((state: any) => state.data.shared.value);

    // Demo video URL for Adding Recipients
    const demoVideoUrl = "https://player.cloudinary.com/embed/?cloud_name=dobeex527&public_id=copy_FCAB8733-3A88-4A83-A3F7-20DE54C94335_1_dg65sz&profile=cld-default";

    useEffect(() => {
        if (userLoginData?.data?.user_data?.no_of_recipients <= sharedData?.data?.recipient_count) {
            window.location.href = '/upgrade';
        }

        DashboardApi.checkContacts().then((response: any) => {
            if (response?.data) {
                setSelectedContacts(response.data);
                console?.log(response.data)
            }
        });

        DashboardApi.getRecipientGroup().then((response: any) => {
            if (response?.data?.data) {
                setGroups(response.data.data);
            }
        });
    }, [showSuccess]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Clear error for this field when user starts typing
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: null });
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                setErrors({ ...errors, profile_image: 'Please select a valid image file (JPG, JPEG, or PNG)' });
                return;
            }

            // Validate file size (optional - e.g., 5MB max)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                setErrors({ ...errors, profile_image: 'Image file size must be less than 5MB' });
                return;
            }

            setFormData({ ...formData, profile_image: file });
            setPreviewImage(URL.createObjectURL(file));
            // Clear any previous image errors
            if (errors.profile_image) {
                setErrors({ ...errors, profile_image: null });
            }
        }
    };

    const isDisabled = (status: any) => {
        const statusMapping: any = {
            'First Contact': 'first_contact',
            'Second Contact': 'second_contact',
            'Third Contact': 'third_contact'
        };

        return selectedContacts[statusMapping[status]] === true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrors({}); // Clear previous errors

        const formDataToSend = new FormData();

        // Append all form fields
        formDataToSend.append('first_name', formData.first_name);
        formDataToSend.append('last_name', formData.last_name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('contact_id', formData.contact_id);
        formDataToSend.append('group_id', formData.group_id);

        // Only append profile_image if it exists and is a File
        if (formData.profile_image && formData.profile_image instanceof File) {
            formDataToSend.append('profile_image', formData.profile_image);
        }

        try {
            const response = await DashboardApi.createRecipient(formDataToSend);

            DashboardApi.getMediaUsage().then(
                (mediaResponse: AxiosResponse<any>) => {
                    if (mediaResponse?.data) {
                        dispatch(shared({ data: mediaResponse?.data?.data }))
                        setShowSuccess(true);
                        setLoading(false);
                        setFormData({
                            first_name: '',
                            last_name: '',
                            email: '',
                            phone: '',
                            contact_id: 'First Contact',
                            group_id: 'Family'
                        });
                        setPreviewImage(''); // Clear preview image

                        // Clear the file input as well
                        const fileInput = document.getElementById('image-upload') as HTMLInputElement;
                        if (fileInput) {
                            fileInput.value = '';
                        }
                    }
                }
            )

        } catch (error: any) {
            setLoading(false);
            console.error('Error creating recipient:', error);

            // Handle validation errors from backend
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            } else if (error.response?.data?.message) {
                setErrors({ general: error.response.data.message });
            } else {
                setErrors({ general: 'An error occurred while creating the recipient' });
            }
        }
    };

    const handleDemoClick = () => {
        setDemoModalOpen(true);
    };

    const closeDemoModal = () => {
        setDemoModalOpen(false);
    };

    // Close modal when clicking outside
    const handleModalBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            closeDemoModal();
        }
    };

    // Close modal on escape key
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && demoModalOpen) {
                closeDemoModal();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [demoModalOpen]);

    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />
                    <div className="p-6 max-w-4xl mx-auto">
                        {/* Header with Demo Button */}
                        <div className="flex justify-between items-center mb-8">
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-semibold">Recipients</h1>
                                <span className="text-orange-500">/</span>
                                <span className="text-orange-500">Add Recipient</span>
                            </div>

                            <div className="flex items-center gap-4">
                                {/* Watch Demo Button with Animations */}
                                <button
                                    onClick={handleDemoClick}
                                    className="group relative px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 animate-pulse"
                                    style={{
                                        animation: 'pulse 2s infinite, glow 3s ease-in-out infinite alternate'
                                    }}
                                >
                                    {/* Animated background */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                    {/* Shimmer effect */}
                                    <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-shimmer"></div>

                                    {/* Button content */}
                                    <div className="relative flex items-center gap-2">
                                        <Play className="w-4 h-4 group-hover:animate-bounce" />
                                        <span className="font-semibold text-sm">Watch Demo</span>
                                    </div>

                                    {/* Floating dots animation */}
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                                </button>
                            </div>
                        </div>

                        {/* Video Modal */}
                        {demoModalOpen && (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm"
                                onClick={handleModalBackdropClick}
                            >
                                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden">
                                    {/* Modal Header */}
                                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            How to Add Recipients - Demo Video
                                        </h3>
                                        <button
                                            onClick={closeDemoModal}
                                            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                                        >
                                            <X className="w-5 h-5 text-gray-600" />
                                        </button>
                                    </div>

                                    {/* Video Container */}
                                    <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
                                        <video
                                            src={'https://res.cloudinary.com/dobeex527/video/upload/v1752477615/copy_FCAB8733-3A88-4A83-A3F7-20DE54C94335_1_dg65sz.mov'}
                                            className="absolute top-0 left-0 w-full h-full object-contain"
                                            controls
                                            preload="metadata"
                                            poster=""
                                            title="Add Recipients Demo Video"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>

                                    {/* Optional: Modal Footer */}
                                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                                        <p className="text-sm text-gray-600">
                                            This demo shows you how to add recipients to your account step by step.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showSuccess && (
                            <div className="bg-green-50 text-green-600 p-4 rounded-lg mb-6 flex justify-between items-center">
                                <span>You have successfully created a new recipient</span>
                                <XCircle className="cursor-pointer" onClick={() => setShowSuccess(false)} />
                            </div>
                        )}

                        {errors.general && (
                            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex justify-between items-center">
                                <span>{errors.general}</span>
                                <XCircle className="cursor-pointer" onClick={() => setErrors({ ...errors, general: null })} />
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex justify-center">
                                <div className="relative">
                                    <img
                                        src={previewImage || "images/profile_image.jpg"}
                                        alt="Profile"
                                        className="w-32 h-32 rounded-full object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('image-upload')?.click()}
                                        className="bg-orange-500 text-white text-[10px] px-4 py-2 rounded-lg absolute -bottom-4 left-1/2 -translate-x-1/2"
                                    >
                                        Update Image
                                    </button>
                                    <input
                                        id="image-upload"
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png"
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>

                            {errors.profile_image && (
                                <div className="text-red-500 text-sm text-center">
                                    {Array.isArray(errors.profile_image) ? errors.profile_image.join(', ') : errors.profile_image}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">First Name</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 border rounded-lg ${errors.first_name ? 'border-red-500' : ''}`}
                                        required
                                    />
                                    {errors.first_name && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {Array.isArray(errors.first_name) ? errors.first_name.join(', ') : errors.first_name}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Last Name</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 border rounded-lg ${errors.last_name ? 'border-red-500' : ''}`}
                                        required
                                    />
                                    {errors.last_name && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {Array.isArray(errors.last_name) ? errors.last_name.join(', ') : errors.last_name}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 border rounded-lg ${errors.email ? 'border-red-500' : ''}`}
                                        required
                                    />
                                    {errors.email && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {Array.isArray(errors.email) ? errors.email.join(', ') : errors.email}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Phone Number</label>
                                    <div className="flex">
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className={`flex-1 p-3 border rounded-lg ${errors.phone ? 'border-red-500' : ''}`}
                                            placeholder="(201) 9875 3892"
                                            required
                                        />
                                    </div>
                                    {errors.phone && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {Array.isArray(errors.phone) ? errors.phone.join(', ') : errors.phone}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Status</label>
                                    <div className="flex gap-4">
                                        {['First Contact', 'Second Contact', 'Third Contact', 'None'].map(status => (
                                            <label key={status} className={`flex items-center gap-2 ${isDisabled(status) ? 'cursor-not-allowed opacity-50' : ''}`}>
                                                <input
                                                    type="radio"
                                                    name="contact_id"
                                                    value={status}
                                                    checked={formData.contact_id == status}
                                                    disabled={isDisabled(status)}
                                                    onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                                                    className="text-blue-600 disabled:opacity-50"
                                                />
                                                <span>{status}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.contact_id && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {Array.isArray(errors.contact_id) ? errors.contact_id.join(', ') : errors.contact_id}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Group</label>
                                    <div className="flex gap-4">
                                        {['Family', 'Friends', 'Spouse', 'Staff', 'Others'].map(group => (
                                            <label key={group} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="group_id"
                                                    value={group}
                                                    checked={formData.group_id === group}
                                                    onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                                                    className="text-blue-600"
                                                />
                                                <span>{group}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.group_id && (
                                        <div className="text-red-500 text-sm mt-1">
                                            {Array.isArray(errors.group_id) ? errors.group_id.join(', ') : errors.group_id}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-12">
                                <button type="button" className="px-6 py-2 text-gray-600">
                                    Cancel
                                </button>
                                <button
                                    disabled={loading || userLoginData?.data?.user_data?.no_of_recipients <= sharedData?.data?.recipient_count}
                                    type="submit"
                                    className="px-6 py-2 bg-orange-500 text-white rounded-lg disabled:opacity-50"
                                >
                                    {loading ? "Please wait..." : (userLoginData?.data?.user_data?.no_of_recipients <= sharedData?.data?.recipient_count ? 'Upgrade' : "Add Recipient")}
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AddRecipient;